import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const StyleContext = createContext(null);

export const useStyles = () => useContext(StyleContext);

// Default styles matching the Style Guide defaults
const defaultStyles = {
  colors: {
    primary: '#1F3C4F',
    primary_hover: '#152a38',
    accent: '#3B82F6',
    header_bg: '#FFFFFF',
    header_text: '#475569',
    footer_bg: '#0F172A',
    footer_text: '#94A3B8',
    hero_bg_from: '#F8FAFC',
    hero_bg_to: '#EFF6FF',
    section_alt_bg: '#F8FAFC',
    heading_text: '#0F172A',
    body_text: '#475569',
    muted_text: '#94A3B8',
    link_color: '#1F3C4F',
    button_bg: '#1F3C4F',
    button_text: '#FFFFFF',
    button_secondary_bg: '#FFFFFF',
    button_secondary_text: '#1F3C4F',
    card_bg: '#FFFFFF',
    card_shadow: 'rgba(0,0,0,0.1)'
  },
  typography: {
    h1_size: '3.75',
    h1_size_mobile: '2.25',
    h1_weight: '700',
    h2_size: '2.25',
    h2_size_mobile: '1.875',
    h2_weight: '700',
    h3_size: '1.5',
    h3_weight: '600',
    body_size: '1',
    body_weight: '400',
    small_size: '0.875',
    line_height: '1.6'
  },
  buttons: {
    border_radius: '0.5',
    padding_x: '1.5',
    padding_y: '0.75',
    font_weight: '500',
    style: 'rounded'
  },
  layout: {
    logo_height: '5',
    logo_height_mobile: '4',
    header_height: '7',
    section_padding_y: '5',
    section_padding_y_mobile: '3',
    card_padding: '2',
    card_border_radius: '1',
    card_shadow_size: 'medium',
    max_width: '80'
  }
};

export const StyleProvider = ({ children }) => {
  const [styles, setStyles] = useState(defaultStyles);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchStyles = async () => {
      try {
        const response = await axios.get(`${API}/public/style-guide`);
        if (response.data && Object.keys(response.data).length > 0) {
          setStyles(prev => ({
            colors: { ...prev.colors, ...response.data.colors },
            typography: { ...prev.typography, ...response.data.typography },
            buttons: { ...prev.buttons, ...response.data.buttons },
            layout: { ...prev.layout, ...response.data.layout }
          }));
        }
      } catch (err) {
        // Use defaults
      } finally {
        setLoaded(true);
      }
    };
    fetchStyles();
  }, []);

  // Generate CSS variables
  useEffect(() => {
    if (!loaded) return;

    const root = document.documentElement;
    
    // Colors
    Object.entries(styles.colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key.replace(/_/g, '-')}`, value);
    });

    // Typography
    Object.entries(styles.typography).forEach(([key, value]) => {
      root.style.setProperty(`--typo-${key.replace(/_/g, '-')}`, value);
    });

    // Buttons
    Object.entries(styles.buttons).forEach(([key, value]) => {
      root.style.setProperty(`--btn-${key.replace(/_/g, '-')}`, value);
    });

    // Layout
    Object.entries(styles.layout).forEach(([key, value]) => {
      root.style.setProperty(`--layout-${key.replace(/_/g, '-')}`, value);
    });

    // Computed button border radius
    const btnRadius = styles.buttons.style === 'pill' ? '9999px' : 
                      styles.buttons.style === 'sharp' ? '0' : 
                      `${styles.buttons.border_radius}rem`;
    root.style.setProperty('--btn-computed-radius', btnRadius);

    // Card shadow
    const shadowMap = {
      none: 'none',
      subtle: '0 1px 3px rgba(0,0,0,0.1)',
      medium: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
      pronounced: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)'
    };
    root.style.setProperty('--card-shadow', shadowMap[styles.layout.card_shadow_size] || shadowMap.medium);

    // Helper function to convert hex to HSL for Shadcn components
    const hexToHsl = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      if (!result) return '210 40% 50%';
      
      let r = parseInt(result[1], 16) / 255;
      let g = parseInt(result[2], 16) / 255;
      let b = parseInt(result[3], 16) / 255;
      
      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      let h, s, l = (max + min) / 2;
      
      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
          case g: h = ((b - r) / d + 2) / 6; break;
          case b: h = ((r - g) / d + 4) / 6; break;
          default: h = 0;
        }
      }
      
      return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
    };

    // Set Shadcn CSS variables based on Style Guide colors
    if (styles.colors.primary) {
      const primaryHsl = hexToHsl(styles.colors.primary);
      root.style.setProperty('--primary', primaryHsl);
      root.style.setProperty('--ring', primaryHsl);
    }
    if (styles.colors.button_bg) {
      const buttonHsl = hexToHsl(styles.colors.button_bg);
      root.style.setProperty('--primary', buttonHsl);
    }

  }, [styles, loaded]);

  return (
    <StyleContext.Provider value={{ styles, loaded }}>
      {children}
    </StyleContext.Provider>
  );
};

export default StyleProvider;
